import { HttpInterceptorFn, HttpRequest, HttpResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { of, throwError, timer } from 'rxjs';
import { delay, mergeMap } from 'rxjs/operators';
import { SessionUser } from './proto/session/session.models';
import { handleProtoRequest, handlePublicRequest, isProtoRequest, isPublicRequest } from './proto/store/proto-api.router';
import { ProtoStoreService } from './proto/store/proto-store.service';

// ── App-specific mock data ───────────────────────────────────────────────────

const MOCK_USERS: any[] = [
  {
    oid: 'usr-001', locked: false,
    email: 'admin@abs-ti.com',
    nombre: 'Admin',
    apellido: 'Demo',
  },
  {
    oid: 'usr-002', locked: false,
    email: 'maria.garcia@example.com',
    nombre: 'Maria',
    apellido: 'Garcia',
  },
  {
    oid: 'usr-003', locked: false,
    email: 'carlos.lopez@example.com',
    nombre: 'Carlos',
    apellido: 'Lopez',
  },
  {
    oid: 'usr-004', locked: false,
    email: 'ana.martinez@example.com',
    nombre: 'Ana',
    apellido: 'Martinez',
  },
  {
    oid: 'usr-005', locked: true,
    email: 'juan.perez@example.com',
    nombre: 'Juan',
    apellido: 'Perez',
  },
];

// ── Service accounts mock data ───────────────────────────────────────────────

const MOCK_SERVICE_ACCOUNTS: any[] = [
  { oid: 'sa-001', userName: 'integracion-crm@service.account', locked: false },
  { oid: 'sa-002', userName: 'jobs-scheduler@service.account', locked: false },
  { oid: 'sa-003', userName: 'reporting-engine@service.account', locked: true },
  { oid: 'sa-004', userName: 'notificaciones@service.account', locked: false },
];

const MOCK_IMPERSONATE_RESPONSE = {
  token: 'mock-jwt-impersonate-token',
  expire: Date.now() + 3600000,
  user: {
    name: 'maria.garcia@example.com',
    oid: 'usr-002',
    serviceAccount: false,
    impersonated: true,
    realUsername: 'admin',
    tenantCode: null,
  },
  details: {
    oid: 'usr-002',
    nombre: 'Maria',
    apellido: 'Garcia',
    email: 'maria.garcia@example.com',
    username: 'maria.garcia',
    role: 'producer',
    canEmit: true,
    memberOid: 'mem-martin',
    treeOid: 'tree-rios',
    treeName: 'Ríos Seguros',
    treeCount: 1,
    treePending: false,
    support: null,
  },
  roles: ['USER', 'ROLE_PRODUCTOR'],
};

// D-037: the one designated username the mock 2FA-challenges on login.
const MOCK_MFA_USERNAME = 'mfa.demo@abs-ti.com';
// D-010-fe: `sales` is 2FA-challenged too; the user is only signed in once the code is verified.
const MOCK_MFA_SELLER_USERNAME = 'sales';
let pendingMfaUsername = 'prod';
const MOCK_MFA_CHALLENGE_ID = 'mock-challenge-001';
const MOCK_MFA_CODE = '123456';

const MOCK_RESET_CHALLENGE_ID = 'mock-reset-challenge-001';
const MOCK_RESET_CODE = '123456';

/** Mocks run before the interceptor chain, so this throws the `ApiError` shape the components expect. */
function mockApiError(status: number, code: string, message: string, url: string) {
  return timer(300).pipe(mergeMap(() => throwError(() => (
    { timestamp: new Date().toISOString(), status, error: 'Error', code, message, path: url }
  ))));
}

function rolesFor(user: SessionUser): string[] {
  switch (user.role) {
    case 'admin':
      return ['ADMIN', 'USER', 'ROLE_IMPERSONATE', 'ROLE_ADMIN_PLATAFORMA'];
    case 'seller':
      return user.canEmit ? ['USER', 'ROLE_VENDEDOR', 'ROLE_EMITIR'] : ['USER', 'ROLE_VENDEDOR'];
    default:
      return ['USER', 'ROLE_PRODUCTOR'];
  }
}

function authResponseFor(user: SessionUser) {
  return {
    token: `mock-jwt-${user.username}`,
    expire: Date.now() + 3600000,
    user: {
      name: user.username,
      oid: user.oid,
      serviceAccount: false,
      impersonated: false,
      realUsername: null,
      tenantCode: null,
    },
    details: user,
    roles: rolesFor(user),
  };
}

/** Tree switch and support mode change who the session is, so they answer with a full auth response. */
function sessionSwitch(req: HttpRequest<unknown>, store: ProtoStoreService) {
  const body = (req.body ?? {}) as Record<string, unknown>;
  const outcome = req.url.endsWith('/session/tree') ? store.selectTree(String(body['treeOid'] ?? ''))
    : req.url.endsWith('/support/enter') ? store.enterSupport(String(body['treeOid'] ?? ''), String(body['reason'] ?? ''))
      : store.exitSupport();
  if ('error' in outcome) return mockApiError(outcome.error.status, outcome.error.code, outcome.error.message, req.url);
  return of(new HttpResponse({ status: 200, body: authResponseFor(outcome.value) })).pipe(delay(200));
}

// Tenant branding fixtures. Customization is kept in sessionStorage so color/logo changes survive
// navigation; uploaded logo bytes stay in memory (a reload falls back to the placeholder PNG).
const MOCK_TENANT_CUSTOMIZATION_KEY = 'tbd_prod_mock_tenant_customization';
const MOCK_TENANT_CUSTOMIZATION_SEED: { primaryColor?: string; logoStorageId?: string } = {
  primaryColor: 'azure',
  logoStorageId: 'mock-logo-001',
};

function getMockTenantCustomization(): { primaryColor?: string; logoStorageId?: string } {
  const stored = sessionStorage.getItem(MOCK_TENANT_CUSTOMIZATION_KEY);
  return stored ? JSON.parse(stored) : MOCK_TENANT_CUSTOMIZATION_SEED;
}

function setMockTenantCustomization(value: { primaryColor?: string; logoStorageId?: string }): void {
  sessionStorage.setItem(MOCK_TENANT_CUSTOMIZATION_KEY, JSON.stringify(value));
}

const MOCK_TENANT = {
  oid: 'tenant-001',
  version: 1,
  code: 'tbd-prod-demo',
  name: 'TBD Prod',
  active: true,
  createUser: 'admin',
  createTimestamp: '2026-01-01T00:00:00Z',
  updateUser: null,
  updateTimestamp: '2026-01-01T00:00:00Z',
  deleteUser: null,
  deleteTimestamp: null,
  activateUser: null,
  activateTimestamp: null,
};

const MOCK_LOGO_PNG_BASE64 =
  'iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAIAAACQd1PeAAAADElEQVR42mNgWDENAAHqAT9kROTJAAAAAElFTkSuQmCC';

const mockUploadedLogos = new Map<string, Blob>();

function mockLogoBlob(fileId: string): Blob {
  const uploaded = mockUploadedLogos.get(fileId);
  if (uploaded) return uploaded;
  const binary = atob(MOCK_LOGO_PNG_BASE64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return new Blob([bytes], { type: 'image/png' });
}

const MOCK_CONFIG = {
  version: '2.0.0-mock',
  captcha: false,
  captchaKey: null,
};

/**
 * App-specific mock interceptor.
 * Handles routes that belong to this app only:
 *   - /api/proto/** (delegated to the in-memory ProtoStoreService)
 *   - /public/2/comparativo/** and /public/2/documentos/** (E-01 / E-02, anonymous, token-scoped, same store)
 *   - /public/2/app/conf
 *   - /public/2/auth/* (the username picks the demo user: prod, sales, admin)
 *   - /api/2/users/me
 *
 * Library-level routes (/api/2/cache, /api/2/security, etc.) are handled
 * by mockLibApiInterceptor, registered after this one and reached via next(req).
 */
export const mockAppApiInterceptor: HttpInterceptorFn = (req, next) => {
  const url = req.url;
  const store = inject(ProtoStoreService);

  if (req.method === 'POST' && /\/api\/proto\/(session\/tree|support\/(enter|exit))$/.test(url)) {
    return sessionSwitch(req, store);
  }

  if (isProtoRequest(url)) {
    return handleProtoRequest(req, store);
  }

  if (isPublicRequest(url)) {
    return handlePublicRequest(req, store);
  }

  // GET /public/2/app/conf
  if (req.method === 'GET' && url.includes('/public/2/app/conf')) {
    return of(new HttpResponse({ status: 200, body: MOCK_CONFIG })).pipe(delay(100));
  }

  // POST /public/2/auth/refresh-token (autoLogin)
  if (req.method === 'POST' && url.includes('/public/2/auth/refresh-token')) {
    const current = store.currentUser();
    if (!current) {
      return mockApiError(401, 'NO_SESSION', 'Sin sesión', url);
    }
    return of(new HttpResponse({ status: 200, body: authResponseFor(current) })).pipe(delay(150));
  }

  // POST /public/2/auth/login/verify — check BEFORE /login below, since it's a substring match
  if (req.method === 'POST' && url.includes('/public/2/auth/login/verify')) {
    const body: any = req.body;
    if (body?.challengeId === MOCK_MFA_CHALLENGE_ID && body?.code === MOCK_MFA_CODE) {
      const blockedAtVerify = store.loginBlock(pendingMfaUsername);
      if (blockedAtVerify) {
        return mockApiError(403, 'USER_SUSPENDED', blockedAtVerify, url);
      }
      const user = store.signIn(pendingMfaUsername);
      return of(new HttpResponse({ status: 200, body: authResponseFor(user!) })).pipe(delay(300));
    }
    return mockApiError(401, 'AUTH_FAILED', 'Código inválido o vencido', url);
  }

  // POST /public/2/auth/login — D-013/D-037: 202 + challenge for the one designated mock username
  if (req.method === 'POST' && url.includes('/public/2/auth/login')) {
    const body: any = req.body;
    const username = String(body?.username ?? '').trim().toLowerCase();
    const blocked = store.loginBlock(username === MOCK_MFA_USERNAME ? 'prod' : username);
    if (blocked) {
      return mockApiError(403, 'USER_SUSPENDED', blocked, url);
    }
    if (username === MOCK_MFA_USERNAME || username === MOCK_MFA_SELLER_USERNAME) {
      pendingMfaUsername = username === MOCK_MFA_SELLER_USERNAME ? 'sales' : 'prod';
      return of(new HttpResponse({
        status: 202,
        body: { mfaRequired: true, challengeId: MOCK_MFA_CHALLENGE_ID, expire: Date.now() + 300000 },
      })).pipe(delay(300));
    }
    const user = store.signIn(username);
    if (!user) {
      return mockApiError(401, 'AUTH_FAILED', 'Usuario o contraseña incorrectos. Probá con prod, sales o admin.', url);
    }
    return of(new HttpResponse({ status: 200, body: authResponseFor(user) })).pipe(delay(300));
  }

  // POST /public/2/auth/logout
  if (req.method === 'POST' && url.includes('/public/2/auth/logout')) {
    store.signOut();
    return of(new HttpResponse({ status: 200, body: null })).pipe(delay(100));
  }

  // POST /public/2/app/reset-password/request — same shape for any email
  if (req.method === 'POST' && url.includes('/public/2/app/reset-password/request')) {
    return of(new HttpResponse({
      status: 200,
      body: { challengeId: MOCK_RESET_CHALLENGE_ID, expire: Date.now() + 300000 },
    })).pipe(delay(300));
  }

  // POST /public/2/app/reset-password/confirm
  if (req.method === 'POST' && url.includes('/public/2/app/reset-password/confirm')) {
    const body: any = req.body;
    if (body?.challengeId === MOCK_RESET_CHALLENGE_ID && body?.code === MOCK_RESET_CODE) {
      return of(new HttpResponse({ status: 204, body: null })).pipe(delay(300));
    }
    return mockApiError(422, 'RESET_CODE_INVALID', 'Invalid or expired code', url);
  }

  // GET /api/2/tenants/current — must precede the /tenant/branding handlers below and, in
  // app.config.ts, the library mock (which would answer it as /tenants/:oid).
  if (req.method === 'GET' && /\/api\/2\/tenants\/current$/.test(url)) {
    const body = {
      ...MOCK_TENANT,
      customization: JSON.stringify(getMockTenantCustomization()),
    };
    return of(new HttpResponse({ status: 200, body })).pipe(delay(150));
  }

  // GET /api/2/tenant/branding/logo/{fileId}
  if (req.method === 'GET' && /\/api\/2\/tenant\/branding\/logo\/[^/]+$/.test(url)) {
    const fileId = url.split('/').pop() ?? '';
    return of(new HttpResponse({ status: 200, body: mockLogoBlob(fileId) })).pipe(delay(150));
  }

  // POST /api/2/tenant/branding/logo (multipart `file`)
  if (req.method === 'POST' && /\/api\/2\/tenant\/branding\/logo$/.test(url)) {
    const file = req.body instanceof FormData ? req.body.get('file') : null;
    const logoStorageId = `mock-logo-${Date.now()}`;
    if (file instanceof Blob) mockUploadedLogos.set(logoStorageId, file);
    setMockTenantCustomization({ ...getMockTenantCustomization(), logoStorageId });
    return of(new HttpResponse({ status: 204, body: null })).pipe(delay(200));
  }

  // PUT /api/2/tenant/branding/color
  if (req.method === 'PUT' && /\/api\/2\/tenant\/branding\/color$/.test(url)) {
    const body = req.body as { primaryColor?: string } | null;
    if (body?.primaryColor) {
      setMockTenantCustomization({ ...getMockTenantCustomization(), primaryColor: body.primaryColor });
    }
    return of(new HttpResponse({ status: 204, body: null })).pipe(delay(150));
  }

  // DELETE /api/2/tenant/branding — resets color AND logo
  if (req.method === 'DELETE' && /\/api\/2\/tenant\/branding$/.test(url)) {
    setMockTenantCustomization({});
    return of(new HttpResponse({ status: 204, body: null })).pipe(delay(150));
  }

  // GET /api/2/users/me
  if (req.method === 'GET' && url.includes('/api/2/users/me')) {
    const current = store.currentUser();
    if (!current) {
      return mockApiError(401, 'NO_SESSION', 'Sin sesión', url);
    }
    return of(new HttpResponse({ status: 200, body: current })).pipe(delay(150));
  }

  // PUT /api/2/users/me/{oid} (saveMisDatos — retargetado al path /me/)
  if (req.method === 'PUT' && /\/api\/2\/users\/me\/[^/]+$/.test(url)) {
    return of(new HttpResponse({ status: 204, body: null })).pipe(delay(200));
  }

  // POST /api/2/users/me/{oid}/change-password (retargetado al path /me/)
  if (req.method === 'POST' && /\/api\/2\/users\/me\/[^/]+\/change-password$/.test(url)) {
    return of(new HttpResponse({ status: 204, body: null })).pipe(delay(200));
  }

  // POST /api/2/auth/impersonate/login
  if (req.method === 'POST' && url.includes('/api/2/auth/impersonate/login')) {
    return of(new HttpResponse({ status: 200, body: MOCK_IMPERSONATE_RESPONSE })).pipe(delay(300));
  }

  // POST /api/2/auth/impersonate/logout (D-040/D-042 — previously missing entirely)
  if (req.method === 'POST' && url.includes('/api/2/auth/impersonate/logout')) {
    const admin = store.currentUser();
    return admin
      ? of(new HttpResponse({ status: 200, body: authResponseFor(admin) })).pipe(delay(300))
      : mockApiError(401, 'NO_SESSION', 'Sin sesión', url);
  }

  // ── Users admin API (/api/2/users/*) ─────────────────────────
  // Orden importa: paths mas especificos ANTES que /{id}

  // GET /api/2/users/service-accounts (lista paginada — ANTES de /users/{id})
  if (req.method === 'GET' && /\/api\/2\/users\/service-accounts$/.test(url)) {
    const params = req.params;
    const filter = (params.get('filter') ?? '').toLowerCase();
    const page = parseInt(params.get('page') ?? '1', 10);
    const size = parseInt(params.get('size') ?? '10', 10);
    const sort = params.get('sort') ?? 'userName';
    const inverse = params.get('inverse') === 'true';
    let filtered = MOCK_SERVICE_ACCOUNTS.filter((sa: any) =>
      !filter || sa.userName.toLowerCase().includes(filter),
    );
    filtered = filtered.sort((a: any, b: any) => {
      const cmp = String(a[sort] ?? '').localeCompare(String(b[sort] ?? ''));
      return inverse ? -cmp : cmp;
    });
    const total = filtered.length;
    const data = filtered.slice((page - 1) * size, page * size);
    return of(new HttpResponse({ status: 200, body: { data, total } })).pipe(delay(200));
  }

  // POST /api/2/users/service-accounts/{oid}/lock
  if (req.method === 'POST' && /\/api\/2\/users\/service-accounts\/[^/]+\/lock$/.test(url)) {
    const oid = url.split('/').slice(-2)[0];
    const sa = MOCK_SERVICE_ACCOUNTS.find((s: any) => s.oid === oid);
    if (sa) sa.locked = true;
    return of(new HttpResponse({ status: 204, body: null })).pipe(delay(150));
  }

  // POST /api/2/users/service-accounts/{oid}/unlock
  if (req.method === 'POST' && /\/api\/2\/users\/service-accounts\/[^/]+\/unlock$/.test(url)) {
    const oid = url.split('/').slice(-2)[0];
    const sa = MOCK_SERVICE_ACCOUNTS.find((s: any) => s.oid === oid);
    if (sa) sa.locked = false;
    return of(new HttpResponse({ status: 204, body: null })).pipe(delay(150));
  }

  // POST /api/2/users/service-accounts/{oid}/secret
  if (req.method === 'POST' && /\/api\/2\/users\/service-accounts\/[^/]+\/secret$/.test(url)) {
    const mockSecret = 'mock-secret-' + 'x'.repeat(40) + '-' + Math.random().toString(36).substring(2, 18);
    return of(new HttpResponse({ status: 200, body: { secret: mockSecret } })).pipe(delay(200));
  }

  // POST /api/2/users/service-accounts/{oid}/renew-secret
  if (req.method === 'POST' && /\/api\/2\/users\/service-accounts\/[^/]+\/renew-secret$/.test(url)) {
    return of(new HttpResponse({ status: 204, body: null })).pipe(delay(200));
  }

  // POST /api/2/users/service-accounts (crear cuenta de servicio)
  if (req.method === 'POST' && /\/api\/2\/users\/service-accounts$/.test(url)) {
    const body: any = req.body;
    const newSa = {
      oid: 'sa-' + Math.random().toString(36).substring(2, 8),
      userName: (body?.userName ?? 'nueva') + '@service.account',
      locked: false,
    };
    MOCK_SERVICE_ACCOUNTS.push(newSa);
    return of(new HttpResponse({ status: 201, body: { oid: newSa.oid } })).pipe(delay(300));
  }

  // GET /api/2/users (lista paginada admin — ANTES de /users/{id})
  if (req.method === 'GET' && /\/api\/2\/users$/.test(url)) {
    const params = req.params;
    const filter = (params.get('filter') ?? '').toLowerCase();
    const page = parseInt(params.get('page') ?? '1', 10);
    const size = parseInt(params.get('size') ?? '10', 10);
    const sort = params.get('sort') ?? 'nombre';
    const inverse = params.get('inverse') === 'true';
    let filtered = MOCK_USERS.filter((u: any) =>
      !filter ||
      u.nombre.toLowerCase().includes(filter) ||
      u.apellido.toLowerCase().includes(filter) ||
      u.email.toLowerCase().includes(filter),
    );
    filtered = filtered.sort((a: any, b: any) => {
      const cmp = String(a[sort] ?? '').localeCompare(String(b[sort] ?? ''));
      return inverse ? -cmp : cmp;
    });
    const total = filtered.length;
    const data = filtered.slice((page - 1) * size, page * size).map((u: any) => ({
      oid: u.oid, nombre: u.nombre, apellido: u.apellido, email: u.email, locked: u.locked,
    }));
    return of(new HttpResponse({ status: 200, body: { data, total } })).pipe(delay(200));
  }

  // POST /api/2/users (admin add — sin oid en path)
  if (req.method === 'POST' && /\/api\/2\/users$/.test(url)) {
    const body: any = req.body;
    const newUser = {
      oid: 'usr-' + Math.random().toString(36).substring(2, 8),
      locked: false,
      email: body?.email ?? '',
      nombre: body?.nombre ?? '',
      apellido: body?.apellido ?? '',
    };
    MOCK_USERS.push(newUser);
    return of(new HttpResponse({ status: 201, body: { oid: newUser.oid } })).pipe(delay(300));
  }

  // PUT /api/2/users/{oid} (admin update)
  if (req.method === 'PUT' && /\/api\/2\/users\/[^/]+$/.test(url)) {
    const oid = url.split('/').pop();
    const user = MOCK_USERS.find((u: any) => u.oid === oid);
    if (user) {
      const body: any = req.body;
      if (body?.nombre) user.nombre = body.nombre;
      if (body?.apellido) user.apellido = body.apellido;
      if (body?.email) user.email = body.email;
    }
    return of(new HttpResponse({ status: 204, body: null })).pipe(delay(200));
  }

  // POST /api/2/users/{oid}/lock
  if (req.method === 'POST' && /\/api\/2\/users\/[^/]+\/lock$/.test(url)) {
    const oid = url.split('/').slice(-2)[0];
    const user = MOCK_USERS.find((u: any) => u.oid === oid);
    if (user) user.locked = true;
    return of(new HttpResponse({ status: 204, body: null })).pipe(delay(150));
  }

  // POST /api/2/users/{oid}/unlock
  if (req.method === 'POST' && /\/api\/2\/users\/[^/]+\/unlock$/.test(url)) {
    const oid = url.split('/').slice(-2)[0];
    const user = MOCK_USERS.find((u: any) => u.oid === oid);
    if (user) user.locked = false;
    return of(new HttpResponse({ status: 204, body: null })).pipe(delay(150));
  }

  // GET /api/2/users/{id} (detalle)
  if (req.method === 'GET' && /\/api\/2\/users\/[^/]+$/.test(url)) {
    const id = url.split('/').pop();
    const user = MOCK_USERS.find((u: any) => u.oid === id) ?? MOCK_USERS[0];
    return of(new HttpResponse({ status: 200, body: user })).pipe(delay(150));
  }

  return next(req);
};
